import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { db, unwrap } from '@/lib/db';
import type {
  Assignment,
  AssignmentRecipient,
  Competency,
  CompetencyEvaluation,
  Course,
  CourseModule,
  Quiz,
  QuizQuestion,
  QuizResult,
} from '@/types/db';

export function useCourses() {
  const { user, activeOrgId } = useAuth();
  return useQuery({
    queryKey: ['courses', 'catalog', activeOrgId ?? 'none'],
    enabled: Boolean(user),
    queryFn: async () => {
      let q = db.from('courses').select('*, modules:course_modules(count)').eq('status', 'published').order('published_at', { ascending: false });
      q = activeOrgId ? q.or(`org_id.is.null,org_id.eq.${activeOrgId}`) : q.is('org_id', null);
      const rows = unwrap(await q) as unknown as (Course & { modules: { count: number }[] })[];
      return rows.map(({ modules, ...course }) => ({ ...course, module_count: modules[0]?.count ?? 0 })) as (Course & { module_count: number })[];
    },
  });
}

export function useCourse(id: string | undefined) {
  return useQuery({
    queryKey: ['courses', 'detail', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const course = unwrap(await db.from('courses').select('*').eq('id', id!).single()) as Course;
      const modules = unwrap(await db.from('course_modules').select('*').eq('course_id', id!).order('position')) as CourseModule[];
      return { course, modules };
    },
  });
}

export function useMyProgress() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['me', 'progress', user?.id],
    enabled: Boolean(user),
    queryFn: async () => {
      const [progress, completions] = await Promise.all([
        db.from('module_progress').select('module_id, course_id, completed_at').eq('user_id', user!.id),
        db.from('course_completions').select('course_id, completed_at, ce_hours, score').eq('user_id', user!.id),
      ]);
      return {
        modules: unwrap(progress) as { module_id: string; course_id: string; completed_at: string }[],
        completions: unwrap(completions) as { course_id: string; completed_at: string; ce_hours: number; score: number | null }[],
      };
    },
  });
}

export function useQuiz(moduleId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['quiz', moduleId, user?.id],
    enabled: Boolean(moduleId && user),
    staleTime: 0,
    queryFn: async () => {
      const quiz = unwrap(await db.from('quizzes').select('*').eq('module_id', moduleId!).single()) as Quiz;
      const [questions, attempts] = await Promise.all([
        db.from('quiz_questions').select('*').eq('quiz_id', quiz.id).order('position'),
        db.from('quiz_attempts').select('id, score, passed, submitted_at').eq('quiz_id', quiz.id).eq('user_id', user!.id).order('submitted_at', { ascending: false }),
      ]);
      return {
        quiz,
        questions: unwrap(questions) as QuizQuestion[],
        attempts: unwrap(attempts) as { id: string; score: number; passed: boolean; submitted_at: string }[],
      };
    },
  });
}

function useInvalidateLearning() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['me'] });
    queryClient.invalidateQueries({ queryKey: ['quiz'] });
  };
}

export function useSubmitQuiz() {
  const invalidate = useInvalidateLearning();
  return useMutation({
    mutationFn: async ({ quizId, answers }: { quizId: string; answers: Record<string, string[]> }) =>
      unwrap(await db.rpc('submit_quiz_attempt', { p_quiz: quizId, p_answers: answers })) as QuizResult,
    onSuccess: invalidate,
  });
}

export function useMarkModuleComplete() {
  const invalidate = useInvalidateLearning();
  return useMutation({
    mutationFn: async (moduleId: string) =>
      unwrap(await db.rpc('mark_module_complete', { p_module: moduleId })) as { course_complete: boolean },
    onSuccess: invalidate,
  });
}

export interface MyAssignment extends AssignmentRecipient {
  assignment: Assignment;
}

export function useMyAssignments() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['me', 'assignments', user?.id],
    enabled: Boolean(user),
    queryFn: async () =>
      unwrap(
        await db
          .from('assignment_recipients')
          .select('*, assignment:assignments(*)')
          .eq('user_id', user!.id)
          .order('assigned_at', { ascending: false }),
      ) as unknown as MyAssignment[],
  });
}

export function useCompleteResourceAssignment() {
  const invalidate = useInvalidateLearning();
  return useMutation({
    mutationFn: async (assignmentId: string) => unwrap(await db.rpc('complete_resource_assignment', { p_assignment: assignmentId })),
    onSuccess: invalidate,
  });
}

export function useMyCompetencies() {
  const { user, activeOrgId } = useAuth();
  return useQuery({
    queryKey: ['me', 'competencies', user?.id, activeOrgId],
    enabled: Boolean(user),
    queryFn: async () => {
      let cq = db.from('competencies').select('*').eq('status', 'published').order('title');
      cq = activeOrgId ? cq.or(`org_id.is.null,org_id.eq.${activeOrgId}`) : cq.is('org_id', null);
      const [competencies, evaluations] = await Promise.all([
        cq,
        db.from('competency_evaluations').select('*').eq('user_id', user!.id).order('evaluated_at', { ascending: false }),
      ]);
      return { competencies: unwrap(competencies) as Competency[], evaluations: unwrap(evaluations) as CompetencyEvaluation[] };
    },
  });
}
